import React from 'react';
import { useStaffOrHasPermission } from '../hooks/useStaffOrHasPermission';
import { getReactSelectInlineStyles } from '../utils/utils';
import { DropdownSelect, type CodeDefinitionOption } from './dropdown-select';
import { InlineEdit } from './inline-edit';

const INLINE_DROPDOWN_STYLES = getReactSelectInlineStyles();

type InlineDropdownEditProps = {
  permission: string;
  value: CodeDefinitionOption | { value: string; label: string } | string | null;
  onChange: (value: CodeDefinitionOption | null) => void;
  options: CodeDefinitionOption[] | undefined;
  placeholder?: string;
  displayValue?: (
    value: CodeDefinitionOption | { value: string; label: string } | string | null
  ) => string;
  small?: boolean;
  disabled?: boolean;
  isClearable?: boolean;
  className?: string;
};

type InlineDropdownEditorProps = {
  val: CodeDefinitionOption | null;
  options: CodeDefinitionOption[] | undefined;
  placeholder: string;
  disabled: boolean;
  isClearable: boolean;
  handleChange: (v: CodeDefinitionOption | null) => void;
  onBlurCommit: () => void;
};

function InlineDropdownEditor({
  val,
  options,
  placeholder,
  disabled,
  isClearable,
  handleChange,
  onBlurCommit,
}: InlineDropdownEditorProps) {
  return (
    <DropdownSelect
      allOptions={options}
      value={val}
      isMulti={false}
      styles={INLINE_DROPDOWN_STYLES}
      onChange={(selectedOption) => {
        const singleValue = Array.isArray(selectedOption)
          ? selectedOption.length > 0
            ? selectedOption[0]
            : null
          : selectedOption;
        handleChange(singleValue as CodeDefinitionOption | null);
        setTimeout(() => {
          onBlurCommit();
        }, 100);
      }}
      placeholder={placeholder}
      isDisabled={disabled}
      menuPortalTarget={document.body}
      isClearable={isClearable}
    />
  );
}

export function InlineDropdownEdit({
  permission,
  value,
  onChange,
  options,
  placeholder = 'Select...',
  displayValue,
  small = false,
  disabled = false,
  isClearable = false,
  className,
}: InlineDropdownEditProps) {
  const canEdit = useStaffOrHasPermission(permission);

  const handleDisplay = (
    val: CodeDefinitionOption | { value: string; label: string } | string | null
  ) => {
    if (displayValue) {
      return displayValue(val);
    }

    if (!val) {
      return placeholder;
    }

    if (typeof val === 'string') {
      const option = options?.find((opt) => opt.value === val);
      return option?.label || val;
    }

    if ('label' in val && val.label) {
      return val.label;
    }

    return placeholder;
  };

  const selectValue = React.useMemo(() => {
    if (!value) return null;

    if (typeof value === 'string') {
      const option = options?.find((opt) => opt.value === value);
      return option || null;
    }

    if ('value' in value && 'label' in value) {
      return value as CodeDefinitionOption;
    }

    return null;
  }, [value, options]);

  return (
    <InlineEdit<CodeDefinitionOption | null>
      readOnly={!canEdit}
      value={selectValue}
      onChange={onChange}
      renderDisplay={handleDisplay}
      renderEditor={(val, handleChange, onBlurCommit) => (
        <InlineDropdownEditor
          val={val}
          options={options}
          placeholder={placeholder}
          disabled={disabled}
          isClearable={isClearable}
          handleChange={handleChange}
          onBlurCommit={onBlurCommit}
        />
      )}
      small={small}
      className={className}
    />
  );
}
